import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { Message } from "@bbwt/classes";
import { FilterInputType, FilterType, IFilterSettings } from "@features/filter";
import { CreateMode, GridComponent, IGridColumn, IGridSettings, ITableSettings } from "@features/grid";
import { FormButtonsConfig } from "@mipro/components";
import { NamingConventionService } from "@mipro/services/naming-convention.service";
import { Squad, SquadType } from "@project/models/squad";
import { ConfirmationService, MessageService } from "primeng/api";
import { SquadService } from "./squad.service";
import { UserHelperService } from "@main/users/user-helper.service";
import { Permissions } from "@main/permissions/permissions";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";
import { PatientLogin } from "@main/users";

@Component({
    selector: "squads-grid",
    templateUrl: "./squads-grid.component.html",
    styleUrls: ["./squads-grid.component.scss"]
})
export class SquadsGridComponent implements OnInit {
    loadingPatients = true;
    private _companyId: string;

    @Input()
    set companyId(id: string) {
        if (this._companyId !== id) {
            this._companyId = id;
            this.reloadGroups();
        }
    }
    get companyId() {
        return this._companyId;
    }

    @ViewChild(GridComponent, { static: true })
    squadsGrid: GridComponent;

    gridSettings: IGridSettings = {
        updateFunc: (squad: Squad) => this.startEditing(squad),
        updateAvailable: _ => this.canUpdateGroup(),
        deleteFunc: squad => this.deleteSquad(squad),
        createMode: CreateMode.Disabled,
        deletingAvailable: _ => this.hasWritePermissions(),
        rowStyleClassFunc: (rowData: Squad) => ({
            "is-archived": rowData.archived
        }),
        actionsColumnWidth: "16rem",
        additionalRowActions: [
            {
                materialIcon: "inventory",
                buttonClass: "p-button-text p-button-rounded p-button-icon-only",
                visible: (rowData: Squad) => !rowData.archived && this.hasWritePermissions(),
                handler: (rowData: Squad) => {
                    this.confirmationService.confirm({
                        message: `Are you sure that you want to archive this ${this.namingConventionService.groupSingular}?`,
                        accept: () =>
                            this.squadService.archive(rowData.id).then(_ => {
                                this.reloadGroups();
                                this.patientService.triggerPatientsUpdated();
                            })
                    });
                }
            },
            {
                materialIcon: "unarchive",
                buttonClass: "p-button-text p-button-rounded p-button-icon-only",
                visible: (rowData: Squad) => rowData.archived && this.hasWritePermissions(),
                handler: (rowData: Squad) => {
                    this.confirmationService.confirm({
                        message: `Are you sure that you want to restore this ${this.namingConventionService.groupSingular}?`,
                        accept: () =>
                            this.squadService.restore(rowData.id).then(_ => {
                                this.reloadGroups();
                                this.patientService.triggerPatientsUpdated();
                            })
                    });
                }
            }
        ]
    };
    tableSettings: ITableSettings = {
        columns: <IGridColumn[]>[
            { field: "name", header: "Name" },
            { field: "notes", header: "Notes" }
        ]
    };
    filterSettings: IFilterSettings[] = [
        { header: "Name", valueFieldName: "name" },
        { header: "Notes", valueFieldName: "notes" },
        { header: "Show Archived", valueFieldName: "showArchived", filterType: FilterType.Boolean, inputType: FilterInputType.Checkbox }
    ];

    editingSquadInProcess = false;
    squadForm: UntypedFormGroup;
    savingSquad = false;
    isCreate = false;
    isArchived = false;

    unassignedPatients: PatientLogin[] = [];
    assignedPatients: PatientLogin[] = [];

    originalName: string;
    originalNotes: string;

    squadFormButtonsConfig = <FormButtonsConfig>{
        cancelFunc: () => {
            this.editingSquadInProcess = false;
        }
    };

    constructor(
        private squadService: SquadService,
        private fb: UntypedFormBuilder,
        private messageService: MessageService,
        public namingConventionService: NamingConventionService,
        private userHelperService: UserHelperService,
        private confirmationService: ConfirmationService,
        private patientService: PatientService
    ) {}

    ngOnInit(): void {
        this.initGroupForm();
        this.gridSettings.dataService = this.squadService;
        this.reloadGroups();
    }

    private reloadGroups() {
        this.squadService.setParentId(this.companyId);
        this.squadsGrid && this.squadsGrid.reload();
    }

    private initGroupForm() {
        this.squadForm = this.fb.group({
            id: [null],
            companyId: [null],
            name: [null, [Validators.required]],
            notes: [null],
            assignedPatientsIds: [[]],
            unassignedPatientsIds: [[]]
        });
    }

    async startEditing(squad?: Squad) {
        try {
            this.loadingPatients = true;
            this.isCreate = squad == null;
            this.isArchived = squad?.archived;
            this.assignedPatients = [];
            this.unassignedPatients = [];
            if (this.isCreate) {
                this.squadForm.reset();
            } else {
                this.squadForm.reset(squad);
                const loadSquad = this.squadService.get(squad.id);
                loadSquad.then(squad => {
                    this.squadForm.patchValue({ ...squad });
                    this.originalName = squad.name;
                    this.originalNotes = squad.notes;
                });
            }

            this.squadService.getPlayers(squad != null ? squad.id : null).then(playersAssignment => {
                this.assignedPatients = playersAssignment.assignedPatients ?? [];
                this.unassignedPatients = playersAssignment.unassignedPatients ?? [];
                this.loadingPatients = false;
            });
        } finally {
            this.editingSquadInProcess = true;
        }
    }

    async saveSquad() {
        if (this.squadForm.invalid) {
            return;
        }

        this.savingSquad = true;

        var assignedPatientsIds = this.assignedPatients.map(assignedPlayer => assignedPlayer.patientId);
        this.squadForm.patchValue({ assignedPatientsIds: assignedPatientsIds });

        try {
            if (this.isCreate) {
                await this.squadService.create(this.squadForm.value);
            } else {
                var unassignedPatientsIds = this.unassignedPatients.map(unassignedPlayer => unassignedPlayer.patientId);
                this.squadForm.patchValue({ unassignedPatientsIds: unassignedPatientsIds });
                if (!this.hasWritePermissions()) {
                    this.squadForm.patchValue({ name: this.originalName, notes: this.originalNotes });
                }
                await this.squadService.update(this.squadForm.get("id").value, this.squadForm.value);
            }

            this.squadsGrid.reload();
            this.editingSquadInProcess = false;
        } catch (error) {
            /* Ignore error */
        } finally {
            this.savingSquad = false;
            this.patientService.triggerPatientsUpdated();
        }
    }

    async deleteSquad(squad: Squad) {
        if (squad.type != SquadType.UserDefined) {
            this.messageService.add(Message.Warning(`Can't delete default ${this.namingConventionService.groupSingular}`, "Forbidden"));
        } else {
            this.squadService.isSquadEmpty(squad.id).then(isEmpty => {
                if (isEmpty) {
                    this.squadService.delete(squad.id).then(() => this.squadsGrid.reload());
                } else {
                    this.messageService.add(
                        Message.Warning(
                            `Cannot delete a ${this.namingConventionService.groupSingular} with ${this.namingConventionService.endUserPlural} allocated - please deallocate ${this.namingConventionService.endUserPlural} first.`,
                            "Forbidden"
                        )
                    );
                }
            });
        }
    }

    addSquad(event: any) {
        event.preventDefault();
        this.startEditing();
    }

    hasWritePermissions(): boolean {
        return this.userHelperService.hasPermission(Permissions.WriteGroups);
    }

    canUpdateGroup(): boolean {
        return (
            this.hasWritePermissions() ||
            (this.userHelperService.hasPermission(Permissions.ReadGroups) && this.userHelperService.hasPermission(Permissions.ReadPatient))
        );
    }
}
